import { Injectable } from '@angular/core';
import { BehaviorSubject, shareReplay } from 'rxjs';
import { BaseService } from '@services/base-service.service';
import {
  IBusinessDataStep,
  ICreditCeilingStep,
  IDistributorDataStep,
  IDocumentStep,
  INoteChangeVerification,
  IPayloadRegistrationVerification,
  IShippingAddressStep,
} from '../../../interfaces/detail-registration.interface';
import { EnumVerificationStatus } from '@pages/sales-marketing/program-marketing-legacy/program-marketing.enum';
import { API } from '@config/constants/api.constant';
import { EnumCardVerificationSection } from './distributor-data/distributor-data.data';
import { AbstractControl } from '@angular/forms';
import { map } from 'rxjs/operators';
import { DistributorService } from '../../../distributor.service';
import { DistributorStatusEnum, DistributorStatusEnumTabString, StatusChangeEnum } from '@config/enum/distributor.enum';
import { RolePrivilegeService } from '@services/role-privilege.service';
import { IResponseApprovalDistributor, IResponseConfirmVerifyDistributor, IResponseVerificationDistributor } from '../../../interfaces/detail-verification.interface';
import { AuthService } from '@services/auth.service';
import { EnumDistributorRevisionSession } from '@config/enum/session.enum';
import { CardVerificationFormService } from '@services/components/card-verification-form.service';

@Injectable({
  providedIn: 'root',
})
export class DetailRegistrationDistributorService {
  private idSubject: BehaviorSubject<string> = new BehaviorSubject('');

  constructor(
    private baseService: BaseService,
    private distributorService: DistributorService,
    private rolePrivilegeService: RolePrivilegeService,
    private authService: AuthService,
    private cardVerificationService: CardVerificationFormService
  ) {}

  get idValue() {
    return this.idSubject.value;
  }

  set idValue(id: string) {
    this.idSubject.next(id);
  }

  isStatusRejectNonActivated() {
    const status = this.distributorService.DistributorDetailHeader.status_enum;
    return status === DistributorStatusEnum.REJECT_NONACTIVATED;
  }

  isStatusNonActivated() {
    const status = this.distributorService.DistributorDetailHeader.status_enum;
    return status === DistributorStatusEnum.NON_ACTIVATED;
  }

  showBadgePendingDetail() {
    if (this.isStatusRejectNonActivated()) {
      return false;
    } else {
      return !this.isStatusNonActivated();
    }
  }

  isSectionVerified = (status?: EnumVerificationStatus | null) => (status ? status === EnumVerificationStatus.VERIFIED : false);

  getDistributorDataStep() {
    return this.baseService
      .getData<{
        distributor_data: IDistributorDataStep;
      }>(API.DISTRIBUTOR.GET_DETAIL_REGISTRATION.DISTRIBUTOR_DATA + this.idValue)
      .pipe(
        shareReplay(),
        map((res) => {
          if (!res || !res.data) return;
          return res.data.distributor_data;
        })
      );
  }

  getBusinessDataStep() {
    return this.baseService
      .getData<{
        business_data: IBusinessDataStep;
      }>(API.DISTRIBUTOR.GET_DETAIL_REGISTRATION.BUSINESS_DATA + this.idValue)
      .pipe(
        shareReplay(),
        map((res) => {
          if (!res || !res.data) return;
          return res.data.business_data;
        })
      );
  }

  getCreditCeilingStep() {
    return this.baseService
      .getData<{
        credit_ceiling: ICreditCeilingStep;
      }>(API.DISTRIBUTOR.GET_DETAIL_REGISTRATION.CREDIT_CEILING + this.idValue)
      .pipe(
        shareReplay(),
        map((res) => {
          if (!res || !res.data) return;
          return res.data.credit_ceiling;
        })
      );
  }

  getShippingAddressStep() {
    return this.baseService.getData<IShippingAddressStep>(API.DISTRIBUTOR.GET_DETAIL_REGISTRATION.DELIVERY_ADDRESS + this.idValue).pipe(
      shareReplay(),
      map((res) => {
        if (!res || !res.data) return;
        return res.data;
      })
    );
  }

  getDocumentStep() {
    return this.baseService.getData<IDocumentStep>(API.DISTRIBUTOR.GET_DETAIL_REGISTRATION.DOCUMENT + this.idValue).pipe(
      shareReplay(),
      map((res) => {
        if (!res || !res.data) return;
        return res.data;
      })
    );
  }

  generateBodyData(data: any, dataBody: any) {
    return this.cardVerificationService.generateBodyData(data, dataBody);
  }

  // IPayloadRegistrationVerification
  setSessionRevisionNote<T>(data: T, key = 'revisionNoteRegistration') {
    sessionStorage.setItem(key, JSON.stringify(data));
  }

  // IPayloadRegistrationDeliveryAddress | undefined
  getSessionRevisionNote(key = 'revisionNoteRegistration') {
    const value = sessionStorage.getItem(key);
    if (!value) return undefined;
    return JSON.parse(value);
  }

  clearSessionBySection(type: EnumCardVerificationSection, key?: string) {
    const _sessionKey = key ?? 'revisionNoteRegistration';
    const data = this.getSessionRevisionNote(_sessionKey);
    if (data) {
      data.revision_note[type.toLowerCase() as keyof typeof data.revision_note] = null;
      this.setSessionRevisionNote(data, _sessionKey);
    }
  }

  clearSessionRevisionNote(key = 'revisionNoteRegistration') {
    sessionStorage.removeItem(key);
  }

  clearAllSessionRevisionNote() {
    sessionStorage.removeItem(EnumDistributorRevisionSession.REGISTRATION_ADMIN);
    sessionStorage.removeItem(EnumDistributorRevisionSession.REGISTRATION_FINANCE);
    sessionStorage.removeItem(EnumDistributorRevisionSession.EDIT_REQUEST_ADMIN);
    sessionStorage.removeItem(EnumDistributorRevisionSession.EDIT_REQUEST_FINANCE);
  }

  updateSectionVerification(
    distributorID: string,
    payload: {
      status: EnumVerificationStatus | null;
      ref_id: string | null;
      type: EnumCardVerificationSection;
    }
  ) {
    return this.baseService.putData<any>(API.DISTRIBUTOR.UPDATE_VERIFICATION_STATUS + distributorID, payload);
  }

  setRevisionData(revisionNoteData: IPayloadRegistrationVerification, e: INoteChangeVerification, key = 'revisionNoteRegistration') {
    const { revision_note } = revisionNoteData;
    const { note, type } = e;
    // Ensure `type` is a valid key in `revision_note` and not 'delivery_address'
    if (Object.prototype.hasOwnProperty.call(revision_note, type.toLowerCase())) {
      // Check if the current value is a string to safely assign a string
      revision_note[type.toLowerCase() as keyof typeof revision_note] = (note as string) ?? '';
    }
    this.setSessionRevisionNote(revisionNoteData, key);
  }

  checkNotHaveRevision = (data: any, statusChange: StatusChangeEnum = StatusChangeEnum.UPDATED) => {
    const status = this.distributorService.DistributorDetailHeader.status_enum;
    if (status === DistributorStatusEnum.PENDING_REGISTRATION) {
      return false;
    } else {
      const haveStatusChange = [StatusChangeEnum.UPDATED, StatusChangeEnum.NOT_CHANGE];
      if (data && haveStatusChange.includes(statusChange)) {
        if (data.branch_list && data.branch_list.length > 0) {
          // for branch check
          const check = data.branch_list.some((branch: any) => branch.revision_data);
          return !check;
        } else {
          return data.revision_data === null;
        }
      } else {
        return false;
      }
    }
  };

  checkValidationFormStepper(control: AbstractControl<any>, revisionType: any) {
    if (!revisionType) return false;
    if (revisionType && revisionType.is_deleted) {
      return true;
    } else {
      return !!(revisionType.status_verified_enum && control) || !!(this.checkNotHaveRevision(revisionType) && control);
    }
  }

  isVerifiedAndRevision(data: any) {
    if (data.is_deleted) {
      return true;
    } else {
      return data.status_verified_enum === EnumVerificationStatus.VERIFIED || this.checkNotHaveRevision(data);
    }
  }

  getTypeRevision(statusTab: string) {
    if (statusTab === DistributorStatusEnumTabString.EDIT_REQUEST) {
      const { status_enum } = this.distributorService.DistributorDetailHeader;
      if (status_enum === DistributorStatusEnum.PENDING_EDIT_VERIFICATION) {
        return EnumDistributorRevisionSession.EDIT_REQUEST_ADMIN;
      } else {
        return EnumDistributorRevisionSession.EDIT_REQUEST_FINANCE;
      }
    } else {
      return EnumDistributorRevisionSession.REGISTRATION_ADMIN;
    }
  }

  checkBtnVerification(statusTab: string) {
    if (statusTab === DistributorStatusEnumTabString.REGISTRATION) {
      if (this.distributorService.DistributorDetailHeader.status_enum === DistributorStatusEnum.PENDING_REGISTRATION) {
        return this.getPrivilegeCTAVerifyRegistrationAdmin();
      }
      if (this.distributorService.DistributorDetailHeader.status_enum === DistributorStatusEnum.PENDING_REGISTRATION_FINANCE) {
        return this.getPrivilegeCTAVerifyRegistrationFinance();
      }
    }

    if (statusTab === DistributorStatusEnumTabString.EDIT_REQUEST) {
      if (this.distributorService.DistributorDetailHeader.status_enum === DistributorStatusEnum.PENDING_EDIT_VERIFICATION) {
        return this.getPrivilegeCTAVerifyEditAdmin();
      }
      if (this.distributorService.DistributorDetailHeader.status_enum === DistributorStatusEnum.PENDING_EDIT_VERIFICATION_FINANCE) {
        return this.getPrivilegeCTAVerifyEditFinance();
      }
    }

    // currently no privilege setting for verification KUL
    // manually set to admin and super admin
    if (statusTab === DistributorStatusEnumTabString.VERIFICATION) {
      return this.authService.isAdminOrSuperAdmin();
    }

    return false;
  }

  submitRegistrationAdmin(payload: IPayloadRegistrationVerification) {
    return this.baseService.postData<IResponseApprovalDistributor>(API.DISTRIBUTOR.POST_REGISTRATION_ADMIN + this.idValue, payload).pipe(shareReplay());
  }

  submitVerificationAdmin(payload: IPayloadRegistrationVerification) {
    return this.baseService.postData<IResponseVerificationDistributor>(API.DISTRIBUTOR.POST_VERIFICATION_ADMIN + this.idValue, payload).pipe(shareReplay());
  }

  submitVerificationFinance(payload: IPayloadRegistrationVerification) {
    return this.baseService.postData<IResponseApprovalDistributor>(API.DISTRIBUTOR.POST_VERIFICATION.APPROVAL_FINANCE + this.idValue, payload).pipe(shareReplay());
  }

  getPrivilegeCTAVerifyRegistrationAdmin() {
    return this.rolePrivilegeService.checkPrivilege('DISTRIBUTOR', 'DETAIL_DISTRIBUTOR', 'CTA_APPROVAL_PENDING_REGISTRATION_ADMIN');
  }

  getPrivilegeCTAVerifyRegistrationFinance() {
    return this.rolePrivilegeService.checkPrivilege('DISTRIBUTOR', 'DETAIL_DISTRIBUTOR', 'CTA_APPROVAL_PENDING_REGISTRATION_FINANCE');
  }

  getPrivilegeCTAVerifyEditAdmin() {
    return this.rolePrivilegeService.checkPrivilege('DISTRIBUTOR', 'DETAIL_DISTRIBUTOR', 'CTA_APPROVAL_PENDING_VERIFICATION_ADMIN');
  }

  getPrivilegeCTAVerifyEditFinance() {
    return this.rolePrivilegeService.checkPrivilege('DISTRIBUTOR', 'DETAIL_DISTRIBUTOR', 'CTA_APPROVAL_PENDING_VERIFICATION_FINANCE');
  }

  getConfirmVerify() {
    return this.baseService.getData<IResponseConfirmVerifyDistributor>(API.DISTRIBUTOR.GET_DETAIL_VERIFICATION.CONFIRM_VERIFY + this.idValue).pipe(
      shareReplay(),
      map((res) => {
        if (res && res.success) {
          return res.data;
        }
      })
    );
  }

  getDeletedPE() {
    return this.baseService
      .getData<{
        product_executive_deleted_list: string[];
      }>(API.DISTRIBUTOR.GET_DETAIL_VERIFICATION.DELETED_PE_LIST + this.idValue)
      .pipe(
        shareReplay(),
        map((res) => {
          if (res && res.success) {
            return res.data.product_executive_deleted_list;
          }
        })
      );
  }

  getDistributorDetailHeader() {
    return this.distributorService.DistributorDetailHeader;
  }
}
